/**
 * Smart Insights dashboard page
 * 
 * This page integrates the Smart Insights data analytics dashboard.
 * Smart Insights uses orchestrator SSE streaming for visual dashboard data.
 */

import { useState, useEffect, useRef, useMemo, startTransition, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Check, Pencil, X } from 'lucide-react';
import { DataSourceModal } from '@/financeBotV2/src/components/dashboard/DataSourceModal';
import { ChatInterface, type InterruptOption } from '@/financeBotV2/src/components/dashboard/ChatInterface';
import { readPersistedAnalysisMode } from '@/financeBotV2/src/utils/analysisMode';
import { LineagePanel, LineageContext } from '@/financeBotV2/src/components/dashboard/LineagePanel';
import { ChartData, DashboardData, KPIMetric } from '@/financeBotV2/src/types/dashboard';
import { api, userAPI } from '@/services/api';
import { transformDashboardResponse } from '@/financeBotV2/src/services/transformers';
import { useQueryStream, type QueryProgress } from '@/financeBotV2/src/hooks/useQueryStream';
import { useGlobalToast } from '../components/UI/toaster';
import { Card, CardContent } from '@/financeBotV2/src/components/ui/card';
import { exportChartsToPPT, exportChartsToPDF } from '@/financeBotV2/src/utils/chartExport';
import { useAuth } from '@/contexts/AuthContext';
import { ConversationMessage, ConversationBookmarkResponse, ConversationService, parseAssistantContent } from '@/services/conversationService';
import { sapOAuthService } from '@/services/sapOAuthService';
import { parseMessageContent } from '@/utils/messageParser';
import { FinanceConversationLayout } from '@/financeBotV2/src/components/dashboard/FinanceConversationLayout';
import { useConversationSidebar } from '@/components/Conversation/useConversationSidebar';

const STREAM_DATA_SOURCE_ERROR_CODES = new Set([
  'SAP_NOT_CONNECTED',
  'SAP_REFRESH_FAILED',
  'SAP_REFRESH_TOKEN_EXPIRED',
  'SAP_TOKEN_EXPIRED',
]);

const normalizeBackendErrorCode = (value?: string | null): string | null => {
  const code = value?.trim().toUpperCase();
  return code || null;
};

const extractBackendErrorCode = (message?: string | null): string | null => {
  if (!message) return null;
  const explicitMatch = message.match(/Error Code:\s*([A-Z0-9_]+)/i);
  if (explicitMatch?.[1]) return explicitMatch[1].toUpperCase();

  const jsonMatch = message.match(/["']error_code["']\s*:\s*["']([A-Z0-9_]+)["']/i);
  if (jsonMatch?.[1]) return jsonMatch[1].toUpperCase();

  const sapCodeMatch = message.match(/\b(SAP_(?:NOT_CONNECTED|REFRESH_FAILED|REFRESH_TOKEN_EXPIRED|TOKEN_EXPIRED|UNAVAILABLE))\b/i);
  return sapCodeMatch?.[1]?.toUpperCase() ?? null;
};

const SMART_INSIGHTS_ROUTE = '/smart-insights';
const SAP_RECONNECT_CONTEXT_STORAGE_KEY = 'smartchg:finance-v2:sap-reconnect-thread-id';
const CHAT_MESSAGE_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type PendingSapReconnectContext = {
  threadId: string | null;
  retryQuery?: string | null;
  reconnectActionLabel?: string | null;
  reconnectErrorMessage?: string | null;
  reconnectErrorMessageId?: string | null;
  reconnectErrorCode?: string | null;
};

type ActiveDataSourceInfo = { name: string; type: string; datasourceId?: string };
type RestoredInterrupt = { runId: string };
type PendingHistoryRecovery = {
  threadId: string;
  messages: ConversationMessage[];
  dataSourceName?: string;
};

const EMPTY_QUERY_PROGRESS: QueryProgress = {
  percent: 0,
  nodes: [],
  completedSteps: [],
  traceSteps: [],
  isComplete: false,
  hasError: false,
};

type LoadActiveDataSourceOptions = {
  attempts?: number;
  delayMs?: number;
  retryWhenMissing?: boolean;
  preserveConnectionResult?: boolean;
};

const wait = (ms: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, ms));

const mapActiveDataSource = (active: any): ActiveDataSourceInfo => {
  const displayType = active.type === 'sap_datasphere' ? 'sap' : String(active.type ?? '');
  return {
    name: String(active.name ?? 'Untitled'),
    type: displayType,
    datasourceId: String(active.datasource_id ?? ''),
  };
};

const restoredInterruptFromMessages = (
  messages: ConversationMessage[],
): RestoredInterrupt | null => {
  const latest = messages[messages.length - 1];
  if (!latest || latest.role !== 'assistant') return null;
  const raw = typeof latest.message === 'string' ? latest.message : latest.content;
  const payload = parseAssistantContent(raw);
  const meta = payload?.meta;
  if (
    payload?.status !== 'clarification'
    || !meta
    || typeof meta !== 'object'
    || (meta as { interrupt?: unknown }).interrupt !== true
  ) return null;
  const runId = (meta as { run_id?: unknown }).run_id;
  if (typeof runId !== 'string' || !runId.trim()) return null;
  return { runId: runId.trim() };
};

const getSmartInsightsRouteWithParams = (params: URLSearchParams): string => {
  const queryString = params.toString();
  return queryString ? `${SMART_INSIGHTS_ROUTE}?${queryString}` : SMART_INSIGHTS_ROUTE;
};

const readPendingSapReconnectContext = (): PendingSapReconnectContext | null => {
  try {
    const stored = window.sessionStorage.getItem(SAP_RECONNECT_CONTEXT_STORAGE_KEY)?.trim();
    if (!stored) return null;
    if (stored.startsWith('{')) {
      const parsed = JSON.parse(stored) as Partial<PendingSapReconnectContext>;
      return {
        threadId: parsed.threadId?.trim() || null,
        retryQuery: parsed.retryQuery?.trim() || null,
        reconnectActionLabel: parsed.reconnectActionLabel?.trim() || null,
        reconnectErrorMessage: parsed.reconnectErrorMessage?.trim() || null,
        reconnectErrorMessageId: parsed.reconnectErrorMessageId?.trim() || null,
        reconnectErrorCode: parsed.reconnectErrorCode?.trim() || null,
      };
    }
    return { threadId: stored, retryQuery: null };
  } catch {
    return null;
  }
};

const writePendingSapReconnectContext = (context?: PendingSapReconnectContext | null): void => {
  try {
    const normalizedThreadId = context?.threadId?.trim() || null;
    const normalizedRetryQuery = context?.retryQuery?.trim() || null;
    const normalizedReconnectActionLabel = context?.reconnectActionLabel?.trim() || null;
    const normalizedReconnectErrorMessage = context?.reconnectErrorMessage?.trim() || null;
    const rawReconnectErrorMessageId = context?.reconnectErrorMessageId?.trim() || null;
    const normalizedReconnectErrorMessageId =
      rawReconnectErrorMessageId && CHAT_MESSAGE_UUID_RE.test(rawReconnectErrorMessageId)
        ? rawReconnectErrorMessageId
        : null;
    const normalizedReconnectErrorCode = context?.reconnectErrorCode?.trim() || null;
    if (
      normalizedThreadId ||
      normalizedRetryQuery ||
      normalizedReconnectActionLabel ||
      normalizedReconnectErrorMessage ||
      normalizedReconnectErrorMessageId ||
      normalizedReconnectErrorCode
    ) {
      window.sessionStorage.setItem(
        SAP_RECONNECT_CONTEXT_STORAGE_KEY,
        JSON.stringify({
          threadId: normalizedThreadId,
          retryQuery: normalizedRetryQuery,
          reconnectActionLabel: normalizedReconnectActionLabel,
          reconnectErrorMessage: normalizedReconnectErrorMessage,
          reconnectErrorMessageId: normalizedReconnectErrorMessageId,
          reconnectErrorCode: normalizedReconnectErrorCode,
        })
      );
    } else {
      window.sessionStorage.removeItem(SAP_RECONNECT_CONTEXT_STORAGE_KEY);
    }
  } catch {
    // Ignore storage failures; the OAuth flow can still complete normally.
  }
};

const clearPendingSapReconnectContext = (): void => {
  writePendingSapReconnectContext(null);
};

const FinanceBotV2Dashboard = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Store dashboard data per query/message - keyed by botMessageId or query
  const [dashboardDataMap, setDashboardDataMap] = useState<Map<string, DashboardData>>(new Map());
  // Keep current dashboard data for backward compatibility
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [currentQuery, setCurrentQuery] = useState<string>('');
  const [dataSourceModalOpen, setDataSourceModalOpen] = useState(false);
  const [activeDataSource, setActiveDataSource] = useState<ActiveDataSourceInfo | null>(null);
  const [hasLoadedActiveDataSource, setHasLoadedActiveDataSource] = useState(false);
  const [dataSourceConnectionResult, setDataSourceConnectionResult] = useState<'connected' | 'error' | 'disconnected' | null>(null);
  const [isDisconnectingDataSource, setIsDisconnectingDataSource] = useState(false);
  const [isProfileNewUser, setIsProfileNewUser] = useState<boolean | null>(null);
  const [welcomeQuestions, setWelcomeQuestions] = useState<string[]>([]);
  const [profileOnboardingUserId, setProfileOnboardingUserId] = useState<string | null>(null);
  const [setupTranscriptResetKey, setSetupTranscriptResetKey] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<'ppt' | 'pdf' | null>(null);
  const [exportProgress, setExportProgress] = useState<{ step: string; current: number; total: number } | null>(null);
  const [chatMessages, setChatMessages] = useState<ConversationMessage[]>([]);
  const [conversationDataSourceName, setConversationDataSourceName] = useState<string | null>(null);
  const [pendingHistoryRecovery, setPendingHistoryRecovery] = useState<PendingHistoryRecovery | null>(null);
  const initialQuestionHandledRef = useRef<string | null>(null);
  const historyLoadGenerationRef = useRef(0);
  const handleMessagesLoadedRef = useRef<(
    messages: ConversationMessage[],
    conversationId?: string,
    meta?: { dataSourceName?: string },
  ) => void>(() => undefined);
  const showStandaloneBookmarksEntry = false;
  const [bookmarksResponse, setBookmarksResponse] = useState<ConversationBookmarkResponse | null>(null);
  const [showBookmarksDropdown, setShowBookmarksDropdown] = useState(false);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);
  const [editingBookmarkId, setEditingBookmarkId] = useState<string | null>(null);
  const [editingBookmarkTitle, setEditingBookmarkTitle] = useState<string>('');
  const [lineageContext, setLineageContext] = useState<LineageContext | null>(null);
  const showKpiLineage = useCallback((metric: KPIMetric) => {
    setLineageContext({ kind: 'kpi', metric });
  }, []);
  const showChartLineage = useCallback((chart: ChartData) => {
    setLineageContext({ kind: 'chart', chart });
  }, []);
  const { success, error } = useGlobalToast();
  const userFullName = useMemo(() => {
    const firstName = user?.firstName?.trim();
    const lastName = user?.lastName?.trim();
    const displayName = user?.displayName?.trim();

    if (firstName && lastName) return `${firstName} ${lastName}`;
    return displayName || firstName || lastName || undefined;
  }, [user?.displayName, user?.firstName, user?.lastName]);
  const hasLoadedProfileOnboarding = Boolean(user?.id) && profileOnboardingUserId === user?.id;

  useEffect(() => {
    let isMounted = true;

    if (!user?.id) {
      setIsProfileNewUser(null);
      setWelcomeQuestions([]);
      setProfileOnboardingUserId(null);
      return () => {
        isMounted = false;
      };
    }

    const profileUserId = user.id;
    setIsProfileNewUser(null);
    setWelcomeQuestions([]);
    setProfileOnboardingUserId(null);

    userAPI.getProfile()
      .then((response) => {
        if (!isMounted) return;
        const profileFlag = response.data?.isNewlyAddedUser;
        setIsProfileNewUser(typeof profileFlag === 'boolean' ? profileFlag : null);
        const questions = response.data?.welcomeQuestions;
        setWelcomeQuestions(Array.isArray(questions) ? questions.filter((question): question is string => typeof question === 'string') : []);
      })
      .catch((err) => {
        console.error('[FinanceBotV2Dashboard] Failed to load profile onboarding flag:', err);
        if (!isMounted) return;
        setIsProfileNewUser(null);
        setWelcomeQuestions([]);
      })
      .finally(() => {
        if (!isMounted) return;
        setProfileOnboardingUserId(profileUserId);
      });

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  const hasRenderableDashboardContent = (data?: DashboardData | null): data is DashboardData => !!data && (
    (data.charts?.length || 0) > 0 ||
    (data.metrics?.length || 0) > 0 ||
    (data.insights?.length || 0) > 0 ||
    (data.actionItems?.length || 0) > 0 ||
    (data.tableData?.length || 0) > 0 ||
    (data.groups?.some((group) => (
      group.charts.length > 0 ||
      group.metrics.length > 0
    )) ?? false)
  );

  // Handle SAP OAuth callback redirect
  useEffect(() => {
    const sapExchange = searchParams.get('sap_exchange');
    const sapError = searchParams.get('sap_error');

    if (sapExchange === 'success') {
      const cleanedSearchParams = new URLSearchParams(searchParams);
      cleanedSearchParams.delete('sap_exchange');
      navigate(getSmartInsightsRouteWithParams(cleanedSearchParams), { replace: true });

      void (async () => {
        const connectedDataSource = await loadActiveDataSource({
          attempts: 2,
          delayMs: 750,
          retryWhenMissing: true,
          preserveConnectionResult: true,
        });
        const reconnectContext = readPendingSapReconnectContext();

        if (connectedDataSource) {
          setDataSourceConnectionResult('connected');
          success('Connected to data sources');
          if (
            reconnectContext?.threadId ||
            reconnectContext?.retryQuery ||
            reconnectContext?.reconnectActionLabel ||
            reconnectContext?.reconnectErrorMessage
          ) {
            void resumeSapReconnectFlow(reconnectContext);
          } else {
            clearPendingSapReconnectContext();
          }
          return;
        }

        setDataSourceConnectionResult('error');
        error("Couldn't connect to data source", 'Please try reconnecting.');
        if (reconnectContext?.threadId) {
          setCurrentConversationId(reconnectContext.threadId);
          void restoreSapReconnectConversation(reconnectContext.threadId, { clearReconnectContext: true });
        } else {
          clearPendingSapReconnectContext();
        }
      })();
    } else if (sapError) {
      setDataSourceConnectionResult('error');
      error('Not connected to data sources');
      const reconnectContext = readPendingSapReconnectContext();
      if (reconnectContext?.threadId) {
        setCurrentConversationId(reconnectContext.threadId);
        void restoreSapReconnectConversation(reconnectContext.threadId, { clearReconnectContext: true });
      } else {
        clearPendingSapReconnectContext();
      }
      
      // Clean up URL parameters
      searchParams.delete('sap_error');
      searchParams.delete('message');
      navigate(getSmartInsightsRouteWithParams(searchParams), { replace: true });
    }
  }, [searchParams, navigate, success, error, dataSourceModalOpen]);

  // Helper: load conversation bookmarks (used on mount and when opening Fav dropdown)
  const loadBookmarks = async () => {
    try {
      setBookmarksLoading(true);
      const res = await ConversationService.getConversationBookmarks();
      setBookmarksResponse(res);
    } catch (err) {
      console.error('[FinanceBotV2Dashboard] Failed to load conversation bookmarks', err);
    } finally {
      setBookmarksLoading(false);
    }
  };

  // Initial load of favorites
  useEffect(() => {
    loadBookmarks();
  }, []);

  // ── Extract dashboard data from any bot message shape ──
  // Returns { data, botMessageId } or null if no dashboard data found.
  const extractDashboard = (obj: any): { data: any; botMessageId: string | null } | null => {
    if (!obj || typeof obj !== 'object') return null;
    const bid = obj.botMessageId || obj.bot_message_id || null;
    if (Array.isArray(obj.blocks)) return { data: obj, botMessageId: bid };
    if (obj.structured && typeof obj.structured === 'object' && Array.isArray(obj.structured.blocks)) {
      return { data: obj.structured, botMessageId: bid };
    }
    if (obj.data?.structured && typeof obj.data.structured === 'object' && Array.isArray(obj.data.structured.blocks)) {
      return { data: obj.data.structured, botMessageId: bid };
    }
    if (Array.isArray(obj.messages)) {
      const assistant = [...obj.messages].reverse().find((m: any) => (
        m?.role === 'assistant' &&
        m?.structured &&
        typeof m.structured === 'object' &&
        Array.isArray(m.structured.blocks)
      ));
      if (assistant) {
        const user = [...obj.messages].reverse().find((m: any) => m?.role === 'user');
        return {
          data: {
            ...assistant.structured,
            query: user?.content || assistant.structured?.meta?.raw_question || obj.thread?.title,
            reply: assistant.content,
            tool_calls: assistant.tool_calls,
          },
          botMessageId: assistant.botMessageId || assistant.bot_message_id || assistant.messageId || assistant.id || bid,
        };
      }
    }
    if (obj.type === 'complete' && obj.data) return { data: obj.data, botMessageId: bid };
    if (obj.data && (obj.data.charts || obj.data.computation_results || obj.data.statistics || obj.data.groups)) return { data: obj.data, botMessageId: bid };
    if (obj.charts || obj.computation_results || obj.statistics || obj.groups) return { data: obj, botMessageId: bid };
    return null;
  };

  // ── Resolve raw content from a CI/ConversationSidebar message ──
  const resolveRawContent = (msg: ConversationMessage, isBotMsg: boolean): any => {
    const msgField = (msg as any).message;
    const contentField = msg.content;
    if (!isBotMsg) return msgField || contentField || '';
    // Bot: prefer message field (contains original JSON)
    if (typeof msgField === 'string' && /^[\[{]/.test(msgField.trim())) return msgField;
    if (typeof contentField === 'string' && /^[\[{]/.test(contentField.trim())) return contentField;
    if (typeof contentField === 'object' && contentField !== null) return contentField;
    if (typeof msgField === 'string') return msgField;
    return contentField || msgField || '';
  };

  const handleMessagesLoaded = (
    loadedMessages: ConversationMessage[],
    conversationIdFromSidebar?: string,
    meta?: { dataSourceName?: string }
  ) => {
    historyLoadGenerationRef.current += 1;
    const restoredInterrupt = conversationIdFromSidebar
      ? restoredInterruptFromMessages(loadedMessages)
      : null;
    // New conversation: clear state
    if (!conversationIdFromSidebar && loadedMessages.length === 0) {
      setPendingHistoryRecovery(null);
      setSetupTranscriptResetKey((key) => key + 1);
      setCurrentConversationId(null);
      setCurrentQuery('');
      setChatMessages([]);
      setConversationDataSourceName(null);
      setDashboardData(null);
      setDashboardDataMap(new Map());
      addedMessageForQueryRef.current = null;
      processedResponseRef.current.clear();
      return;
    }

    if (conversationIdFromSidebar) {
      const keepRunningStream = Boolean(isLoading && currentQuerySessionRef.current);
      const returningToRunningStream = Boolean(
        keepRunningStream && conversationIdFromSidebar === streamConversationId
      );
      if (!keepRunningStream) {
        currentQuerySessionRef.current = null;
        setStreamThreadId(conversationIdFromSidebar, restoredInterrupt);
      } else if (returningToRunningStream) {
        addedMessageForQueryRef.current = null;
        processedResponseRef.current.clear();
      }
      setSetupTranscriptResetKey((key) => key + 1);
      setCurrentConversationId(conversationIdFromSidebar);
      setConversationDataSourceName(meta?.dataSourceName ?? null);
      setDashboardData(null);
      setDashboardDataMap(new Map());
    }

    // Normalize CI timestamps (epoch seconds → ISO)
    const normalizeTs = (ts: any): string => {
      if (ts == null) return new Date().toISOString();
      if (typeof ts === 'number') {
        return new Date(ts < 946684800000 ? Math.round(ts * 1000) : Math.round(ts)).toISOString();
      }
      if (typeof ts === 'string') {
        const t = ts.trim();
        if (/^\d+(\.\d+)?$/.test(t)) {
          const n = Number(t);
          return new Date(n < 946684800000 ? Math.round(n * 1000) : Math.round(n)).toISOString();
        }
        const d = new Date(t);
        if (!Number.isNaN(d.getTime())) return d.toISOString();
      }
      return new Date().toISOString();
    };

    // Extract query from first user message
    const firstUser = loadedMessages.find(m => m.role === 'user');
    if (firstUser) {
      const uc = firstUser.content || (firstUser as any).message || '';
      const q = typeof uc === 'string' ? uc : (uc as any)?.query || (uc as any)?.message || '';
      if (q) setCurrentQuery(q);
    }

    const allDashboardDataMap = new Map<string, { data: any; botMessageId: string | null }>();

    const transformedMessages = loadedMessages.map((msg, index) => {
      // Normalize role
      let role = msg.role as 'user' | 'assistant' | 'system' | undefined;
      if (!role) {
        const s = String((msg as any).senderType || (msg as any).sender_type || (msg as any).sender || '').toUpperCase();
        if (s === 'HUMAN') role = 'user';
        else if (s === 'BOT') role = 'assistant';
        else if (s === 'SYSTEM') role = 'system';
        if (role) (msg as any).role = role;
      }
      const isBotMsg = role === 'assistant';
      const isUserMsg = role === 'user';

      const rawContent = resolveRawContent(msg, isBotMsg);

      let parsedContent: any = null;
      let dashboardInfo: { data: any; botMessageId: string | null } | null = null;
      let persistedInterruptOptions: InterruptOption[] | undefined;

      if (isUserMsg) {
        parsedContent = { content: typeof rawContent === 'string' ? rawContent : String(rawContent ?? ''), isJson: false };
      } else if (isBotMsg) {
        // Try to extract dashboard data from the raw content
        let obj = rawContent;
        if (typeof rawContent === 'string') {
          const trimmed = rawContent.trim();
          if (/^[\[{]/.test(trimmed)) {
            try { obj = JSON.parse(rawContent); } catch { obj = rawContent; }
          }
        }
        // If first parse didn't yield dashboard data, try message field as fallback
        dashboardInfo = extractDashboard(obj);
        if (!dashboardInfo && typeof (msg as any).message === 'string') {
          try {
            const fallback = JSON.parse((msg as any).message);
            dashboardInfo = extractDashboard(fallback);
            if (dashboardInfo) obj = fallback;
          } catch { /* not JSON */ }
        }
        parsedContent = parseMessageContent(typeof obj === 'object' ? obj : rawContent);
        if (
          restoredInterrupt
          && index === loadedMessages.length - 1
          && Array.isArray(obj?.meta?.interrupt_options)
        ) {
          persistedInterruptOptions = obj.meta.interrupt_options.filter(
            (option: unknown): option is InterruptOption => Boolean(
              option
              && typeof option === 'object'
              && typeof (option as { action?: unknown }).action === 'string'
            ),
          );
        }

        if (dashboardInfo) {
          const key = dashboardInfo.botMessageId || msg.messageId || `dashboard-${index}`;
          allDashboardDataMap.set(key, dashboardInfo);
        }
      } else {
        parsedContent = parseMessageContent(rawContent);
      }

      // Resolve message ID. The legacy ``dashboardInfo.botMessageId`` only
      // exists when the persisted dashboard JSON happened to embed it —
      // most rows don't, so we fall back to the persisted ``messageId``
      // (= the canonical ``messages.message_id`` UUID returned by the chat
      // GET endpoint and forwarded by ConversationService.mapMessage). Without
      // this fallback historical bot turns end up with botMessageId=null and
      // the feedback button never renders (it gates on ``!!botMessageId``).
      const botMsgId = dashboardInfo?.botMessageId || msg.botMessageId || msg.messageId || null;
      let messageId: string;
      if (isBotMsg) {
        messageId = botMsgId || msg.messageId || `msg-${new Date(normalizeTs(msg.timestamp)).getTime()}-${index}-ai`;
      } else {
        messageId = msg.messageId || `msg-${new Date(normalizeTs(msg.timestamp)).getTime()}-${index}-user`;
      }

      // Resolve final text content
      let finalContent = '';
      if (typeof parsedContent === 'object' && parsedContent !== null) finalContent = parsedContent.content || '';
      else if (typeof parsedContent === 'string') finalContent = parsedContent;
      if (isUserMsg && !finalContent) {
        const dm = (msg as any).message || msg.content || '';
        finalContent = typeof dm === 'string' ? dm : String(dm);
      }
      if (isBotMsg && !finalContent) finalContent = 'Analysis complete! View results in the dashboard.';

      let rawContentText = '';
      if (typeof rawContent === 'string') {
        rawContentText = rawContent;
      } else if (rawContent !== null && rawContent !== undefined) {
        try {
          rawContentText = JSON.stringify(rawContent);
        } catch {
          rawContentText = String(rawContent);
        }
      }
      const rawContentErrorCode = typeof rawContent === 'object' && rawContent !== null
        ? normalizeBackendErrorCode(
            (rawContent as any).errorCode ??
            (rawContent as any).error_code ??
            (rawContent as any).data?.errorCode ??
            (rawContent as any).data?.error_code,
          )
        : null;
      const parsedContentErrorCode = typeof parsedContent === 'object' && parsedContent !== null
        ? normalizeBackendErrorCode(
            (parsedContent as any).errorCode ??
            (parsedContent as any).error_code ??
            (parsedContent as any).data?.errorCode ??
            (parsedContent as any).data?.error_code,
          )
        : null;
      const messageErrorCode = isBotMsg
        ? (
            normalizeBackendErrorCode(
              (msg as any).errorCode ??
              (msg as any).error_code ??
              (msg as any).error?.errorCode ??
              (msg as any).error?.error_code,
            ) ??
            rawContentErrorCode ??
            parsedContentErrorCode ??
            extractBackendErrorCode(rawContentText) ??
            extractBackendErrorCode(finalContent)
          )
        : null;
      const messageStatus = isBotMsg && messageErrorCode && STREAM_DATA_SOURCE_ERROR_CODES.has(messageErrorCode)
        ? 'sap_refresh_failed'
        : (msg as any).status;

      return {
        id: messageId,
        messageId,
        role: (role || 'system') as 'user' | 'assistant' | 'system',
        content: typeof finalContent === 'string' ? finalContent : String(finalContent ?? ''),
        timestamp: normalizeTs(msg.timestamp),
        botMessageId: botMsgId,
        ...(messageStatus ? { status: messageStatus } : {}),
        ...(messageErrorCode ? { errorCode: messageErrorCode, error_code: messageErrorCode } : {}),
        ...(Array.isArray((msg as any).suggestedQueries) && (msg as any).suggestedQueries.length
          ? { suggestedQueries: (msg as any).suggestedQueries } : {}),
        ...(persistedInterruptOptions?.length ? { interruptOptions: persistedInterruptOptions } : {}),
        // Carry forward any persisted feedback so ChatInterface can pre-fill
        // the modal when the user clicks the feedback button on a past turn.
        ...(isBotMsg && (msg as any).feedback ? { feedback: (msg as any).feedback } : {}),
      };
    });

    // Transform all dashboard data and attach to messages
    const transformedDashboards = new Map<string, DashboardData>();

    if (allDashboardDataMap.size > 0) {
      allDashboardDataMap.forEach((info, key) => {
        try {
          const d = info.data;
          const resp = {
            query: d.query || currentQuery || '',
            task: d.task,
            summary: d.summary,
            normal_text: d.normal_text,
            charts: d.charts || [], insights: d.insights || [],
            statistics: d.statistics, source_data: d.source_data,
            source_data_metadata: d.source_data_metadata,
            intelligence_analysis: d.intelligence_analysis,
            computation_results: d.computation_results,
            sql_plan: d.sql_plan, generated_sql: d.generated_sql,
            selected_tables: d.selected_tables, status: 'completed',
            aggregations: d.aggregations, operation_plan: d.operation_plan,
            chart_plan: d.chart_plan, query_id: d.query_id,
            analysis_summary: d.analysis_summary,
            groups: d.groups,
            blocks: d.blocks,
            $schema: d.$schema,
            meta: d.meta,
            query_context: d.query_context,
            reply: d.reply,
            tool_calls: d.tool_calls,
            suggested_queries: d.suggested_queries,
          };
          const td = transformDashboardResponse(resp as any);
          const dk = info.botMessageId || key;
          const entry = { ...td, botMessageId: info.botMessageId || undefined };
          transformedDashboards.set(dk, entry);
          setDashboardDataMap(prev => { const m = new Map(prev); m.set(dk, entry); return m; });
        } catch (err) {
          console.error('[FinanceBotV2Dashboard] Error processing dashboard:', key, err);
        }
      });

      // Set latest dashboard as current
      const latest = Array.from(allDashboardDataMap.entries()).pop();
      if (latest) {
        const dk = latest[1].botMessageId || latest[0];
        const td = transformedDashboards.get(dk);
        if (td) {
          setDashboardData(td);
          if (td.query) setCurrentQuery(td.query);
        }
      }
    }

    // Attach dashboard data to bot messages
    const messagesWithDashboards = transformedMessages.map(msg => {
      if (msg.role === 'assistant') {
        let dd: DashboardData | undefined;
        if (msg.botMessageId && transformedDashboards.has(msg.botMessageId)) {
          dd = transformedDashboards.get(msg.botMessageId);
        } else {
          const fk = Array.from(transformedDashboards.keys()).find(k => String(k) === String(msg.id));
          if (fk) dd = transformedDashboards.get(fk);
        }
        return dd ? { ...msg, dashboardData: dd } : msg;
      }
      return msg;
    });

    setChatMessages(messagesWithDashboards);
    setPendingHistoryRecovery(
      conversationIdFromSidebar && loadedMessages[loadedMessages.length - 1]?.role === 'user'
        ? {
            threadId: conversationIdFromSidebar,
            messages: loadedMessages,
            dataSourceName: meta?.dataSourceName,
          }
        : null,
    );
  };
  handleMessagesLoadedRef.current = handleMessagesLoaded;

  async function restoreSapReconnectConversation(
    threadId: string,
    options: { clearReconnectContext?: boolean } = {}
  ) {
    try {
      const response = await ConversationService.getConversationMessages(threadId);
      if (response.success && response.messages) {
        const thread = (response as { thread?: { data_source_name?: string | null } }).thread;
        const dataSourceName = thread?.data_source_name ?? undefined;
        handleMessagesLoaded(
          response.messages,
          threadId,
          dataSourceName ? { dataSourceName } : undefined,
        );
      }
    } catch (err) {
      console.error('[FinanceBotV2Dashboard] Failed to restore SAP reconnect conversation:', err);
    } finally {
      if (options.clearReconnectContext !== false) {
        clearPendingSapReconnectContext();
      }
    }
  }

  const appendSapReconnectConfirmation = (
    context: Pick<
      PendingSapReconnectContext,
      'reconnectActionLabel' | 'reconnectErrorMessage' | 'reconnectErrorMessageId' | 'reconnectErrorCode'
    > = {},
  ) => {
    const normalizedReconnectActionLabel = context.reconnectActionLabel?.trim();
    const normalizedReconnectErrorMessage = context.reconnectErrorMessage?.trim();
    const rawReconnectErrorMessageId = context.reconnectErrorMessageId?.trim();
    const normalizedReconnectErrorMessageId =
      rawReconnectErrorMessageId && CHAT_MESSAGE_UUID_RE.test(rawReconnectErrorMessageId)
        ? rawReconnectErrorMessageId
        : null;
    const normalizedReconnectErrorCode = normalizeBackendErrorCode(context.reconnectErrorCode);
    setChatMessages(prev => {
      const now = Date.now();
      const hasReconnectErrorMessage = normalizedReconnectErrorMessage
        ? prev.some((msg) => {
            const msgId = msg.messageId || (msg as any).id || msg.botMessageId || '';
            if (normalizedReconnectErrorMessageId && msgId === normalizedReconnectErrorMessageId) {
              return true;
            }
            const msgContent = String(msg.content || (msg as any).message || '').trim();
            const msgErrorCode =
              normalizeBackendErrorCode((msg as any).errorCode ?? (msg as any).error_code) ??
              extractBackendErrorCode(msgContent);
            return (
              msg.role === 'assistant' &&
              msgContent === normalizedReconnectErrorMessage &&
              Boolean(msgErrorCode && STREAM_DATA_SOURCE_ERROR_CODES.has(msgErrorCode))
            );
          })
        : false;
      const reconnectErrorMessage: ConversationMessage[] = normalizedReconnectErrorMessage && !hasReconnectErrorMessage
        ? [{
            messageId: normalizedReconnectErrorMessageId || `sap-refresh-failed-restored-${now}`,
            role: 'assistant' as const,
            content: normalizedReconnectErrorMessage,
            timestamp: new Date(now).toISOString(),
            botMessageId: normalizedReconnectErrorMessageId || undefined,
            status: 'sap_refresh_failed',
            ...(normalizedReconnectErrorCode
              ? { errorCode: normalizedReconnectErrorCode, error_code: normalizedReconnectErrorCode }
              : {}),
          }]
        : [];
      const reconnectActionMessage: ConversationMessage[] = normalizedReconnectActionLabel
        ? [{
            messageId: `sap-reconnect-action-${now + 1}`,
            role: 'user' as const,
            content: normalizedReconnectActionLabel,
            timestamp: new Date(now + 1).toISOString(),
            isLiveQuery: true,
            status: 'sap_reconnect_action',
          }]
        : [];

      return [
        ...prev,
        ...reconnectErrorMessage,
        ...reconnectActionMessage,
        {
          messageId: `sap-reconnected-${now + 2}`,
          role: 'assistant' as const,
          content: 'Data source reconnected. Retrying your question now.',
          timestamp: new Date(now + 2).toISOString(),
          isLiveQuery: true,
          status: 'sap_reconnected',
        },
      ];
    });
  };

  async function resumeSapReconnectFlow(context: PendingSapReconnectContext) {
    const reconnectThreadId = context.threadId?.trim() || null;
    const retryQuery = context.retryQuery?.trim() || null;
    const reconnectActionLabel = context.reconnectActionLabel?.trim() || null;
    const reconnectErrorMessage = context.reconnectErrorMessage?.trim() || null;
    const reconnectErrorMessageId = context.reconnectErrorMessageId?.trim() || null;
    const reconnectErrorCode = context.reconnectErrorCode?.trim() || null;

    if (reconnectThreadId) {
      setCurrentConversationId(reconnectThreadId);
      await restoreSapReconnectConversation(reconnectThreadId, { clearReconnectContext: false });
    }

    const reconnectContextForMessages = {
      reconnectActionLabel,
      reconnectErrorMessage,
      reconnectErrorMessageId,
      reconnectErrorCode,
    };

    if (retryQuery) {
      appendSapReconnectConfirmation(reconnectContextForMessages);
      handleQuery(retryQuery, readPersistedAnalysisMode(), { threadIdOverride: reconnectThreadId });
    } else if (reconnectActionLabel || reconnectErrorMessage) {
      appendSapReconnectConfirmation(reconnectContextForMessages);
    }

    clearPendingSapReconnectContext();
  }

  const cachedResponseRef = useRef<string | null>(null);
  const processedResponseRef = useRef<Set<string>>(new Set()); // Track processed responses to prevent duplicates
  const shownErrorRef = useRef<string | null>(null); // Track last shown error to prevent duplicate toasts
  const interruptActionInFlightRef = useRef(false);
  const userId = user?.id || 'default-user';
  // Track current conversation ID for sending follow-up messages in the same session.
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);

  // SSE-based query hook for live progress streaming
  const {
    sendQuery: sendStreamQuery,
    progress,
    response: streamResponse,
    botMessageId,
    humanMessageId,  // Human message ID from Communication Infrastructure for message linking
    currentQuery: streamCurrentQuery,  // Current query from hook for message linking
    conversationId: streamConversationId,  // Conversation ID from stream for session management
    canResumeInterrupt,
    setThreadId: setStreamThreadId,
    isLoading,
    isConnected,  // SSE stream open flag — flips false only on onclose/error.
                  // Used to keep the live trace card visible through final →
                  // saved → complete (the entire stream lifetime) instead of
                  // dismissing it the moment the FIRST kpi_card_ready frame
                  // sets a partial dashboard.
    error: streamError,
    reset: resetStream,  // Abort in-flight request and clear hook state when starting new conversation
  } = useQueryStream();
  const isViewingStreamConversation = Boolean(
    !streamConversationId
    || !currentConversationId
    || streamConversationId === currentConversationId
  );
  const isBackgroundStreamRunning = isLoading && !isViewingStreamConversation;

  useEffect(() => {
    const pending = pendingHistoryRecovery;
    if (
      !pending
      || isLoading
      || currentConversationId !== pending.threadId
    ) return;

    const controller = new AbortController();
    const historyLoadGeneration = historyLoadGenerationRef.current;
    // ponytail: bounded history polling recovers browser refreshes; durable
    // server-restart recovery needs persisted job replay.
    void (async () => {
      for (let attempt = 0; attempt < 100; attempt += 1) {
        if (
          controller.signal.aborted
          || historyLoadGenerationRef.current !== historyLoadGeneration
        ) return null;
        const response = await ConversationService.getConversationMessages(
          pending.threadId,
          { signal: controller.signal, timeoutMs: 10_000 },
        );
        const messages = response.success ? response.messages : null;
        if (
          messages
          && messages.slice(pending.messages.length).some((message) => message.role === 'assistant')
        ) return messages;
        if (attempt < 99) await wait(1_500);
      }
      return null;
    })().then((messages) => {
      if (
        controller.signal.aborted
        || historyLoadGenerationRef.current !== historyLoadGeneration
      ) return;
      if (!messages) {
        setPendingHistoryRecovery((current) => current === pending ? null : current);
        return;
      }
      handleMessagesLoadedRef.current(
        messages,
        pending.threadId,
        pending.dataSourceName ? { dataSourceName: pending.dataSourceName } : undefined,
      );
    });

    return () => controller.abort();
  }, [currentConversationId, isLoading, pendingHistoryRecovery]);

  const {
    isSidebarOpen,
    openSidebar,
    closeSidebar,
    handleSelectConversation,
    handleStartNewConversation: resetSidebarConversation,
  } = useConversationSidebar(handleMessagesLoaded);

  const handleStartNewConversation = useCallback(() => {
    setSetupTranscriptResetKey((key) => key + 1);
    resetStream();
    setCurrentQuery('');
    setDashboardData(null);
    setDashboardDataMap(new Map());
    setChatMessages([]);
    setConversationDataSourceName(null);
    setCurrentConversationId(null);
    addedMessageForQueryRef.current = null;
    processedResponseRef.current.clear();
    cachedResponseRef.current = null;
    currentQuerySessionRef.current = null;
    currentUserMessageIdRef.current = null;
    pendingUserMessageIdsRef.current.clear();
    shownErrorRef.current = null;

    resetSidebarConversation();
  }, [resetSidebarConversation, resetStream]);

  const handleConnectDataSourceFromWelcome = useCallback(async (context?: {
    threadId?: string | null;
    retryQuery?: string | null;
    reconnectActionLabel?: string | null;
    reconnectErrorMessage?: string | null;
    reconnectErrorMessageId?: string | null;
    reconnectErrorCode?: string | null;
  }) => {
    if (!hasLoadedProfileOnboarding || isProfileNewUser === null) {
      return;
    }

    const reconnectThreadId =
      context?.threadId?.trim() ||
      currentConversationId?.trim() ||
      streamConversationId?.trim() ||
      null;
    setDataSourceConnectionResult(null);
    writePendingSapReconnectContext({
      threadId: reconnectThreadId,
      retryQuery: context?.retryQuery?.trim() || null,
      reconnectActionLabel: context?.reconnectActionLabel?.trim() || null,
      reconnectErrorMessage: context?.reconnectErrorMessage?.trim() || null,
      reconnectErrorMessageId: context?.reconnectErrorMessageId?.trim() || null,
      reconnectErrorCode: context?.reconnectErrorCode?.trim() || null,
    });

    try {
      // Mirrors the DataSourceModal reconnect action without opening the modal.
      await sapOAuthService.initiateConnection('finance-v2');
    } catch (err: any) {
      clearPendingSapReconnectContext();
      setDataSourceConnectionResult('error');
      error('Not connected to data sources');
      throw err;
    }
  }, [currentConversationId, error, hasLoadedProfileOnboarding, isProfileNewUser, streamConversationId]);

  const handleDisconnectActiveDataSource = useCallback(async () => {
    const datasourceId = activeDataSource?.datasourceId;
    if (!datasourceId) {
      error('Disconnect unavailable', 'The active data source id is not available. Refresh and try again.');
      return;
    }

    if (!window.confirm('Disconnect the active data source for now? You can reconnect it again from SmartInsights.')) {
      return;
    }

    try {
      setIsDisconnectingDataSource(true);
      await api.patch(`/api/v1/datasources/${datasourceId}/activation`, { is_active: false });
      setActiveDataSource(null);
      setDataSourceConnectionResult('disconnected');
      success('Data source disconnected', 'The active data source has been disconnected.');
    } catch (err: any) {
      console.error('Failed to disconnect active data source:', err);
      error('Disconnect Failed', err?.message || 'Failed to disconnect the active data source. Please try again.');
    } finally {
      setIsDisconnectingDataSource(false);
    }
  }, [activeDataSource?.datasourceId, error, success]);

  // Initialize live data dependencies on mount.
  // Smart Insights should not restore dashboard responses from frontend cache:
  // every submitted prompt must go through the chat-v2 Stream API.
  useEffect(() => {
    const initialSearchParams = new URLSearchParams(window.location.search);
    const isSapCallback =
      initialSearchParams.get('sap_exchange') === 'success' ||
      initialSearchParams.has('sap_error');

    if (!isSapCallback) {
      loadActiveDataSource();
    }

  }, []);

  async function loadActiveDataSource(options: LoadActiveDataSourceOptions = {}): Promise<ActiveDataSourceInfo | null> {
    // Source the active datasource from the SmartCHG catalog.
    const attempts = Math.max(1, options.attempts ?? 1);
    const delayMs = options.delayMs ?? 0;
    let receivedDatasourceResponse = false;
    const clearConnectionResult = () => {
      if (options.preserveConnectionResult) return;
      setDataSourceConnectionResult((current) => {
        if (current === 'connected') return current;
        if (current === 'error') return 'connected';
        return null;
      });
    };
    setHasLoadedActiveDataSource(false);
    try {
      for (let attempt = 1; attempt <= attempts; attempt += 1) {
        try {
          const response = await api.get('/api/v1/datasources');
          receivedDatasourceResponse = true;
          const remote: any[] = Array.isArray(response.data) ? response.data : [];
          const active = remote.find((d) => d?.is_active === true);
          if (active) {
            // Map type='sap_datasphere' to the user-facing 'sap' label that
            // the rest of the UI uses (matches the small-cap badge).
            const mappedActiveDataSource = mapActiveDataSource(active);
            setActiveDataSource(mappedActiveDataSource);
            clearConnectionResult();
            return mappedActiveDataSource;
          }
        } catch (error) {
          console.error('Failed to load active data source:', error);
        }

        if (!options.retryWhenMissing || attempt === attempts || delayMs <= 0) {
          break;
        }

        await wait(delayMs);
      }

      setActiveDataSource(null);
      if (receivedDatasourceResponse) {
        clearConnectionResult();
      }
      return null;
    } finally {
      setHasLoadedActiveDataSource(true);
    }
  }

  // Memoize transformed data to avoid re-computation on every render
  const transformedData = useMemo(() => {
    if (!streamResponse) return null;
    return transformDashboardResponse(streamResponse);
  }, [streamResponse]);

  // Memoize dashboard key to avoid recalculation
  // Use a stable key based on botMessageId or query, not Date.now() to prevent re-renders
  const dashboardKey = useMemo(() => {
    if (!streamResponse) return null;
    // Use botMessageId if available (most stable)
    if (botMessageId) return botMessageId;
    // Use query if available
    if (streamResponse.query) return `dashboard-${streamResponse.query}`;
    // Only use timestamp as last resort, and memoize it per response
    return null; // Don't create dashboard if we don't have a stable key
  }, [streamResponse?.query, botMessageId]);

  // Memoize data with botMessageId
  const dataWithBotMessageId = useMemo(() => {
    if (!transformedData || !dashboardKey) return null;
    return {
      ...transformedData,
      botMessageId: botMessageId || undefined,
    };
  }, [transformedData, botMessageId, dashboardKey]);

  // Track if we've already added a message for the current query to prevent duplicates
  // This now stores: { queryKey: string, messageId: string } to track both the query and the message ID
  const addedMessageForQueryRef = useRef<{ queryKey: string; messageId: string } | null>(null);
  
  // Unique session ID for each query - ensures we can distinguish queries with same content
  // This is set when handleQuery is called and used to create unique message IDs
  const currentQuerySessionRef = useRef<string | null>(null);
  
  // Track temporary IDs for ALL pending user queries (query content -> temp ID)
  // This allows us to handle multiple queries sent before humanMessageIds arrive
  const pendingUserMessageIdsRef = useRef<Map<string, string>>(new Map());
  
  // Track the temporary ID for the LATEST user query (for backward compatibility)
  // This will be updated to the real humanMessageId when received from Communication Infrastructure
  const currentUserMessageIdRef = useRef<string | null>(null);

  // Handle stream response when complete - use startTransition for non-urgent updates
  useEffect(() => {
    if (!streamResponse) return;
    if (
      streamConversationId
      && currentConversationId
      && streamConversationId !== currentConversationId
    ) return;
    const querySession = currentQuerySessionRef.current;
    if (!querySession) return;
    const queryContent = streamCurrentQuery || streamResponse.query || '';

    const normalText = (streamResponse as any).normal_text || (streamResponse as any).data?.normal_text;
    const hasDashboardContent = hasRenderableDashboardContent(transformedData);

    if (normalText && !hasDashboardContent) {
      const queryKey = querySession;
      const responseId = `normal-${queryKey}-${normalText.substring(0, 50)}`;
      if (processedResponseRef.current.has(responseId)) return;
      processedResponseRef.current.add(responseId);
      if (processedResponseRef.current.size > 10) {
        const entries = Array.from(processedResponseRef.current);
        processedResponseRef.current.clear();
        entries.slice(-10).forEach(id => processedResponseRef.current.add(id));
      }

      const messageId = botMessageId || `normal-text-${queryKey}-${Date.now()}`;
      addedMessageForQueryRef.current = { queryKey, messageId };
      let actualHumanMessageId = humanMessageId;
      if (!actualHumanMessageId && queryContent) {
        actualHumanMessageId = pendingUserMessageIdsRef.current.get(queryContent) || currentUserMessageIdRef.current;
      }
      const normalSuggested = (streamResponse as any).suggested_queries ?? (streamResponse as any).data?.suggested_queries;
      const normalSuggestedList = Array.isArray(normalSuggested) ? normalSuggested : (normalSuggested ? [normalSuggested] : []);
      const interruptOptions = (streamResponse as any).meta?.interrupt_options;
      const botMessage: ConversationMessage = {
        id: messageId,
        role: 'assistant',
        content: normalText,
        timestamp: new Date().toISOString(),
        botMessageId: botMessageId || undefined,
        humanMessageId: actualHumanMessageId || undefined,
        isLiveQuery: true,
        status: (streamResponse as any).status || 'clarification',
        suggestedQueries: normalSuggestedList.length ? normalSuggestedList : undefined,
        interruptOptions: Array.isArray(interruptOptions) ? interruptOptions : undefined,
      };
      startTransition(() => setChatMessages(prev => (
        currentQuerySessionRef.current === querySession
          ? [...prev, botMessage]
          : prev
      )));
      return;
    }

    // Dashboard path: require all memoized deps
    if (!transformedData || !dataWithBotMessageId || !dashboardKey) return;
    
    // One query session owns one assistant result even when a resumed stream's
    // provisional query text is replaced by the final resolved task.
    // Use session ID to uniquely identify this query (allows same content to be queried multiple times)
    const queryKey = querySession;
    const chartsCount = streamResponse.charts?.length || 0;
    const metricsCount = transformedData.metrics?.length || 0;
    const visualGroupsCount = transformedData.groups?.filter((group) => (
      group.charts.length > 0 ||
      group.metrics.length > 0 ||
      Boolean(group.summary?.trim())
    )).length || 0;
    const summaryChars = transformedData.summaryText?.trim().length || 0;
    // The Executive Summary arrives after the KPI/chart frames. It is a
    // user-visible revision even though none of the dashboard item counts
    // change. Include its exact content in the de-duplication identity so the
    // later summary frame (and the authoritative final envelope) updates the
    // existing canvas instead of being discarded as a duplicate.
    const executiveSummary = transformedData.summaryText?.trim() || '';
    const responseId = `${queryKey}-${streamResponse.status || 'pending'}-charts:${chartsCount}-metrics:${metricsCount}-groups:${visualGroupsCount}-summaryChars:${summaryChars}-normalText:${normalText ? 'yes' : 'no'}-executiveSummary:${executiveSummary}`;
    
    if (processedResponseRef.current.has(responseId)) {
      if (botMessageId && addedMessageForQueryRef.current?.queryKey === queryKey) {
        const dashboardMsgId = addedMessageForQueryRef.current.messageId;
        startTransition(() => {
          setChatMessages(prev => {
            if (currentQuerySessionRef.current !== querySession) return prev;
            const idx = prev.findIndex(m => m.id === dashboardMsgId);
            if (idx >= 0 && prev[idx].botMessageId !== botMessageId) {
              const updated = [...prev];
              updated[idx] = { ...updated[idx], botMessageId };
              return updated;
            }
            return prev;
          });
        });
      }
      return;
    }
    
    // Mark this response as processed
    processedResponseRef.current.add(responseId);
    
    // Clean up old processed responses (keep only last 10 to prevent memory leak)
    if (processedResponseRef.current.size > 10) {
      const entries = Array.from(processedResponseRef.current);
      processedResponseRef.current.clear();
      entries.slice(-10).forEach(id => processedResponseRef.current.add(id));
    }
    
    // Only update dashboardDataMap if there's actual visual dashboard content.
    // For normal_text-only responses, skip this so ChatInterface doesn't treat it as an empty dashboard
    const hasDashboardContentHere = hasRenderableDashboardContent(transformedData);

    if (hasDashboardContentHere) {
      startTransition(() => {
        setDashboardDataMap(prev => {
          if (currentQuerySessionRef.current !== querySession) return prev;
          const newMap = new Map(prev);
          newMap.set(dashboardKey, dataWithBotMessageId);
          return newMap;
        });
        setDashboardData(prev => (
          currentQuerySessionRef.current === querySession
            ? dataWithBotMessageId
            : prev
        ));
      });
    }

    // Check if response is complete based on status
    const isResponseComplete = 
      streamResponse.status === 'completed' ||
      streamResponse.status === 'success' ||
      streamResponse.status === 'response_orchestrated';

    const responseKey = `${streamResponse.query || ''}_${streamResponse.status || ''}`;

    // Add/update bot response in chat messages when we have actual dashboard content.
    // Normal_text-only responses were already handled at the start of this effect.
    if (hasDashboardContentHere) {
      // Use a UNIQUE ID for this query's dashboard message
      // CRITICAL: Use timestamp-based ID to ensure we don't accidentally match old messages
      const queryMessageId = `dashboard-${queryKey}-${Date.now()}`;
      
      // Check if we've already added a message for THIS SPECIFIC query session
      // The queryKey now includes the session ID, so this ensures we only match the current query
      // and not previous queries with the same content
      if (addedMessageForQueryRef.current?.queryKey === queryKey) {
        const dashboardMsgId = addedMessageForQueryRef.current.messageId;
        startTransition(() => {
          setChatMessages(prev => {
            if (currentQuerySessionRef.current !== querySession) return prev;
            const idx = prev.findIndex(m => m.id === dashboardMsgId);
            if (idx >= 0) {
              const updated = [...prev];
              const suggested = (streamResponse as any).suggested_queries ?? (streamResponse as any).data?.suggested_queries;
              const suggestedList = Array.isArray(suggested) ? suggested : (suggested ? [suggested] : undefined);
              updated[idx] = {
                ...updated[idx],
                dashboardData: dataWithBotMessageId,
                botMessageId: botMessageId || updated[idx].botMessageId,
                ...(suggestedList?.length ? { suggestedQueries: suggestedList } : {}),
              };
              return updated;
            }
            return prev;
          });
        });
        return;
      }

      const messageId = botMessageId || queryMessageId;
      addedMessageForQueryRef.current = { queryKey, messageId };

      startTransition(() => {
        let actualHumanMessageId = humanMessageId;
        if (!actualHumanMessageId && queryContent) {
          actualHumanMessageId = pendingUserMessageIdsRef.current.get(queryContent) || currentUserMessageIdRef.current;
        }
        const suggestedQueries = (streamResponse as any).suggested_queries ?? (streamResponse as any).data?.suggested_queries;
        const suggestedQueriesList = Array.isArray(suggestedQueries) ? suggestedQueries : (suggestedQueries ? [suggestedQueries] : undefined);
        const botMessage: ConversationMessage = {
          id: messageId,
          role: 'assistant',
          content: '',
          timestamp: new Date().toISOString(),
          botMessageId: botMessageId || undefined,
          humanMessageId: actualHumanMessageId || undefined,
          dashboardData: dataWithBotMessageId,
          isLiveQuery: true,
          ...(suggestedQueriesList?.length ? { suggestedQueries: suggestedQueriesList } : {}),
        };
        setChatMessages(prev => (
          currentQuerySessionRef.current === querySession
            ? [...prev, botMessage]
            : prev
        ));
      });
    }

    // Show toast only once when complete (not on every partial update)
    // Use the already computed isResponseComplete from above
    if (isResponseComplete && (transformedData.metrics.length > 0 || transformedData.charts.length > 0)) {
      // Use a ref to track if we've shown the toast for this response
      const toastKey = `${streamResponse.query || ''}_${streamResponse.status || ''}`;
      if (cachedResponseRef.current !== toastKey) {
        success(
          "Analysis Complete",
          `Found ${transformedData.metrics.length} metrics and ${transformedData.charts.length} charts.`
        );
        cachedResponseRef.current = toastKey;
      }
    }
  }, [
    streamResponse,
    transformedData,
    dataWithBotMessageId,
    dashboardKey,
    botMessageId,
    streamCurrentQuery,
    streamConversationId,
    currentConversationId,
  ]);

  // Handle stream errors - show backend error messages directly and stop loading
  useEffect(() => {
    // Check both streamError from hook and progress.errorMessage from backend
    // Prefer progress.errorMessage as it comes directly from the backend stream error message
    const backendErrorMessage = progress.hasError && progress.errorMessage 
      ? progress.errorMessage 
      : streamError;
    
    if (backendErrorMessage) {
      let errorMessage: string;
      let errorTitle = 'Error';
      if (typeof backendErrorMessage === 'string') {
        errorMessage = backendErrorMessage;
      } else if (typeof backendErrorMessage === 'object' && backendErrorMessage !== null) {
        errorMessage = 'message' in backendErrorMessage ? String((backendErrorMessage as any).message) : String(backendErrorMessage);
        if ('error_category' in backendErrorMessage && (backendErrorMessage as any).error_category) {
          errorTitle = String((backendErrorMessage as any).error_category);
        }
      } else {
        errorMessage = String(backendErrorMessage);
      }

      if ((errorMessage || '').trim().toLowerCase() === 'structured response with data') return;

      const backendErrorCode = extractBackendErrorCode(errorMessage);
      if (backendErrorCode === 'SAP_UNAVAILABLE') {
        const unavailableTitle = 'Data source temporarily unavailable';
        const unavailableMessage = 'Please try again in a moment.';
        const errorKey = `${unavailableTitle}:${unavailableMessage}`;
        if (shownErrorRef.current === errorKey) return;
        shownErrorRef.current = errorKey;
        error(unavailableTitle, unavailableMessage);
        return;
      }

      if (backendErrorCode && STREAM_DATA_SOURCE_ERROR_CODES.has(backendErrorCode)) {
        setDataSourceConnectionResult('error');
        return;
      }

      const errorKey = `${errorTitle}:${errorMessage}`;
      if (shownErrorRef.current === errorKey) return;
      shownErrorRef.current = errorKey;
      error(errorTitle, errorMessage);
    } else {
      shownErrorRef.current = null;
    }
  }, [streamError, progress.hasError, progress.errorMessage]);

  useEffect(() => {
    if (streamConversationId) setCurrentConversationId(streamConversationId);
  }, [streamConversationId]);

  useEffect(() => {
    if (!isLoading) interruptActionInFlightRef.current = false;
  }, [isLoading]);
  
  useEffect(() => {
    if (humanMessageId && streamCurrentQuery) {
      const queryContent = streamCurrentQuery;
      let tempId = pendingUserMessageIdsRef.current.get(queryContent) || currentUserMessageIdRef.current;
      if (!tempId) return;

      setChatMessages(prev => {
        let updated = [...prev];
        let userMsgUpdated = false;

        const idx = prev.findIndex(m => m.id === tempId);
        if (idx >= 0) {
          updated[idx] = { ...updated[idx], id: humanMessageId, messageId: humanMessageId };
          userMsgUpdated = true;
        } else {
          const ci = prev.findIndex(m => m.role === 'user' && m.content === queryContent);
          if (ci >= 0) {
            updated[ci] = { ...updated[ci], id: humanMessageId, messageId: humanMessageId };
            userMsgUpdated = true;
          }
        }

        if (userMsgUpdated) {
          updated = updated.map(m =>
            m.role === 'assistant' && m.humanMessageId === tempId
              ? { ...m, humanMessageId }
              : m
          );
        }
        return updated;
      });
      
      // Remove from pending map after successful update
      pendingUserMessageIdsRef.current.delete(queryContent);
      
      // Update the ref for backward compatibility (only if this is the current query)
      if (currentUserMessageIdRef.current === tempId) {
        currentUserMessageIdRef.current = humanMessageId;
      }
    }
  }, [humanMessageId, streamCurrentQuery]);

  function handleQuery(
    query: string,
    // Default reads localStorage so suggested-question clicks
    // (``onSuggestedQueryClick={handleQuery}``) — which invoke this
    // with only the ``query`` arg — still pick up the user's selected
    // Normal / Deep effort. Default parameter values are evaluated at
    // each call, so this picks up the latest persisted choice.
    analysisMode: 'normal' | 'deep' = readPersistedAnalysisMode(),
    options: { threadIdOverride?: string | null; resumeOverride?: Record<string, any>; displayQuery?: string } = {}
  ) {
    setDashboardData(null);
    addedMessageForQueryRef.current = null;
    processedResponseRef.current.clear();
    const targetConversationId = options.threadIdOverride ?? currentConversationId ?? undefined;
    if (options.threadIdOverride) {
      setCurrentConversationId(options.threadIdOverride);
    }

    currentQuerySessionRef.current = `${Date.now()}-${Math.random().toString(36).substring(7)}`;
    const displayQuery = options.displayQuery || query;
    setCurrentQuery(displayQuery);
    cachedResponseRef.current = null;

    const tempUserMessageId = `temp-user-${Date.now()}`;
    currentUserMessageIdRef.current = tempUserMessageId;
    pendingUserMessageIdsRef.current.set(displayQuery, tempUserMessageId);

    // Always append a fresh user turn. Repeated questions must not be
    // visually deduped because each submit sends a new chat-v2 stream request.
    setChatMessages(prev => [
      ...prev,
      { id: tempUserMessageId, role: 'user' as const, content: displayQuery, timestamp: new Date().toISOString(), isLiveQuery: true },
    ]);

    // Send query with conversation ID. Active datasource context is resolved by the SmartCHG backend.
    sendStreamQuery(query, userId, analysisMode, targetConversationId || undefined, options.resumeOverride);
  }

  useEffect(() => {
    const question = searchParams.get('question')?.trim();
    if (!question || initialQuestionHandledRef.current === question) return;

    initialQuestionHandledRef.current = question;
    handleQuery(question, 'normal');

    const cleanedSearchParams = new URLSearchParams(searchParams);
    cleanedSearchParams.delete('question');
    navigate(getSmartInsightsRouteWithParams(cleanedSearchParams), { replace: true });
  // The ref and URL cleanup make this a one-shot handoff even though handleQuery is recreated per render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, searchParams]);

  function handleInterruptAction(action: string, label?: string, value?: string) {
    if (interruptActionInFlightRef.current || !canResumeInterrupt()) return;
    interruptActionInFlightRef.current = true;
    setChatMessages(prev => prev.map(message => (
      message.interruptOptions
        ? { ...message, interruptOptions: undefined }
        : message
    )));
    const displayText = label || value || action;
    const answer = value || label || action;
    handleQuery(answer, readPersistedAnalysisMode(), {
      displayQuery: displayText,
      resumeOverride: action === 'clarify'
        ? { action, question: answer, display_text: displayText }
        : { action, display_text: displayText },
    });
  }

  const handleExport = async (format: 'ppt' | 'pdf') => {
    if (!dashboardData || !dashboardData.charts || dashboardData.charts.length === 0) {
      error(
        "No Charts Available",
        "There are no charts to export. Please generate some charts first."
      );
      return;
    }

    setIsExporting(true);
    setExportFormat(format);
    const totalSteps = dashboardData.charts.length + (dashboardData.insights && dashboardData.insights.length > 0 ? 2 : 1);
    setExportProgress({ step: 'Initializing...', current: 0, total: totalSteps });

    try {
      const progressCallback = (step: string, current: number, total: number) => {
        setExportProgress({ step, current, total });
      };

      if (format === 'ppt') {
        await exportChartsToPPT(dashboardData.charts as any, dashboardData.query, dashboardData.insights, progressCallback);
        success(
          "Export Complete",
          "Your charts have been exported to PowerPoint."
        );
      } else if (format === 'pdf') {
        await exportChartsToPDF(dashboardData.charts as any, dashboardData.query, dashboardData.insights, progressCallback);
        success(
          "Export Complete",
          "Your charts have been exported to PDF."
        );
      }
    } catch (err) {
      error(
        "Export Failed",
        err instanceof Error ? err.message : "Failed to export charts."
      );
    } finally {
      setIsExporting(false);
      setExportFormat(null);
      setExportProgress(null);
    }
  };


  return (
    <div className="relative flex h-full w-full flex-col overflow-auto bg-[#FAF7F1] text-[#1F1A20]">
      {/* Favorites dropdown for saved finance conversations (opens just below the Fav button) */}
      {showBookmarksDropdown && (
        <div className="absolute right-4 top-[4.25rem] z-50 flex h-[min(28rem,calc(100vh-7rem))] w-[min(25rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-[14px] border border-[#EDE6E1] bg-white shadow-[0_4px_6px_-1px_rgba(31,26,32,.06),0_12px_28px_-10px_rgba(110,52,80,.18)] sm:right-6">
            {/* Repo-2 branding pass: v2-only saved conversations popover. */}
            <div className="flex shrink-0 items-start justify-between border-b border-[#EDE6E1] bg-white px-4 py-3">
              <div className="min-w-0">
                <div className="font-display text-[13px] font-bold uppercase leading-tight tracking-[0.22em] text-[#7B6B72]">
                  SAVED THREADS
                </div>
                <div className="mt-1 text-[12.5px] leading-tight text-[#7B6B72]">
                  {bookmarksResponse?.bookmarks?.length ?? 0} saved · click to open
                </div>
              </div>
              <button
                type="button"
                className="rounded-[8px] p-1 text-[#7B6B72] transition-colors hover:bg-[#FAF7F1] hover:text-[#1F1A20]"
                onClick={() => setShowBookmarksDropdown(false)}
                title="Close saved threads"
                aria-label="Close saved threads"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto bg-[#fbf8f4] p-3">
            {bookmarksLoading && (
              <div className="flex h-full items-center justify-center py-8">
                <div className="text-center">
                  <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-4 border-[#e7c8d2] border-t-[#6E3450]" />
                  <span className="text-xs font-medium text-[#7B6B72]">Loading saved threads...</span>
                </div>
              </div>
            )}
            {!bookmarksLoading && bookmarksResponse?.bookmarks && bookmarksResponse.bookmarks.length > 0 && bookmarksResponse.bookmarks.map((bm) => {
              const isEditing = editingBookmarkId === bm.id;
              const updatedAtLabel = new Date(bm.updatedAt).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              });
              return (
                <div
                  key={bm.id}
                  className="group relative border-l-2 border-transparent px-3 py-3 text-xs transition-colors hover:border-[#6E3450] hover:bg-[#f5e9ee]/65"
                >
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        value={editingBookmarkTitle}
                        onChange={(e) => setEditingBookmarkTitle(e.target.value)}
                        className="w-full rounded border border-[#6E3450]/40 bg-white px-2 py-1 text-[12.5px] text-[#1F1A20] outline-none focus:border-[#6E3450]"
                        autoFocus
                      />
                      <div className="mt-2 flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          className="rounded p-1 text-[#7B6B72] transition-colors hover:bg-white hover:text-[#1F1A20]"
                          onClick={() => setEditingBookmarkId(null)}
                          title="Cancel rename"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          className="rounded p-1 text-[#7B6B72] transition-colors hover:bg-white hover:text-[#3F7D4F]"
                          title="Save rename"
                          onClick={async () => {
                            const trimmed = editingBookmarkTitle.trim();
                            if (!trimmed) {
                              setEditingBookmarkId(null);
                              return;
                            }
                            const oldTitle = bm.title;
                            try {
                              const renamed = await ConversationService.renameConversationBookmark(bm.id, trimmed);
                              if (renamed) {
                                setBookmarksResponse((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        bookmarks: prev.bookmarks.map((b) =>
                                          b.id === bm.id ? { ...b, title: renamed.title, updatedAt: renamed.updatedAt } : b
                                        ),
                                      }
                                    : prev
                                );
                                success(
                                  'Favorite renamed',
                                  `"${oldTitle}" → "${renamed.title}"`
                                );
                              }
                            } catch (err) {
                              console.error('[FinanceBotV2Dashboard] Failed to rename bookmark', err);
                              error('Rename failed', 'Could not rename favorite. Please try again.');
                            } finally {
                              setEditingBookmarkId(null);
                            }
                          }}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="mt-1 text-[10px] text-[#A8989F]">
                        {updatedAtLabel}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-2">
                        <button
                          type="button"
                          className="min-w-0 flex-1 truncate text-left font-display text-[12.5px] font-medium leading-tight text-[#1F1A20]"
                          onClick={async () => {
                            try {
                              const res = await ConversationService.getConversationMessages(bm.conversationId);
                              if (res.success && res.messages) {
                                handleMessagesLoaded(res.messages, bm.conversationId);
                                setShowBookmarksDropdown(false);
                              }
                            } catch (err) {
                              console.error('[FinanceBotV2Dashboard] Failed to load bookmarked conversation', err);
                            }
                          }}
                          title={bm.title}
                        >
                          {bm.title}
                        </button>
                        <span className="shrink-0 whitespace-nowrap text-[10px] text-[#A8989F]">
                          {updatedAtLabel}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="mt-2 inline-flex items-center gap-1 rounded text-[10.5px] font-medium uppercase tracking-[0.12em] text-[#7B6B72] transition-colors hover:text-[#6E3450]"
                        title="Rename favorite"
                        onClick={() => {
                          setEditingBookmarkId(bm.id);
                          setEditingBookmarkTitle(bm.title);
                        }}
                      >
                        <Pencil className="h-3 w-3" />
                        Rename
                      </button>
                    </>
                  )}
                </div>
              );
            })}
            {!bookmarksLoading && (!!bookmarksResponse && bookmarksResponse.bookmarks.length === 0) && (
              <div className="flex h-full items-center justify-center px-5 py-10 text-center text-[12.5px] italic text-[#7B6B72]">
                No saved threads yet.
              </div>
            )}
            </div>
          </div>
        )}

      <DataSourceModal
        open={dataSourceModalOpen}
        onOpenChange={(open) => {
          setDataSourceModalOpen(open);
          if (!open) {
            loadActiveDataSource();
          }
        }}
        userId={userId}
        sapSource="finance-v2"
      />

      <main className="flex-1 flex flex-col overflow-hidden min-h-0 relative">
        {/* UnifiedChatWrapper always visible - wraps both chat and dashboard views */}
        {/* Show Smart Insights conversations from the shared finance thread table. */}
        <FinanceConversationLayout
          isSidebarOpen={isSidebarOpen}
          onCloseSidebar={closeSidebar}
          onSelectConversation={handleSelectConversation}
          onStartNewConversation={handleStartNewConversation}
          currentConversationId={currentConversationId}
        >
          <>
            {/* Chat Interface - Always visible, shows all queries and dashboards inline */}
            <div className="flex-1 flex flex-col overflow-hidden min-h-0">
              <ChatInterface
                key={currentConversationId ?? 'new-conversation'}
                userQuery={currentQuery}
                progress={isViewingStreamConversation ? progress : EMPTY_QUERY_PROGRESS}
                isLoading={isViewingStreamConversation && isLoading}
                welcomeQuestions={welcomeQuestions}
                isConnected={isViewingStreamConversation && isConnected}
                threadId={currentConversationId}
                botMessageId={isViewingStreamConversation ? botMessageId : null}
                conversationMessages={chatMessages}
                dashboardDataMap={dashboardDataMap}
                currentDashboardData={dashboardData}
                conversationDataSourceName={conversationDataSourceName ?? undefined}
                activeDataSourceName={activeDataSource?.name}
                onSuggestedQueryClick={isBackgroundStreamRunning ? undefined : handleQuery}
                onSubmit={isBackgroundStreamRunning ? undefined : handleQuery}
                onInterruptAction={isViewingStreamConversation ? handleInterruptAction : undefined}
                onExport={handleExport}
                onDataSourceClick={() => setDataSourceModalOpen(true)}
                onConnectDataSourceClick={
                  hasLoadedProfileOnboarding && isProfileNewUser !== null
                    ? handleConnectDataSourceFromWelcome
                    : undefined
                }
                onDisconnectDataSourceClick={handleDisconnectActiveDataSource}
                activeDataSource={activeDataSource}
                hasLoadedActiveDataSource={hasLoadedActiveDataSource}
                dataSourceConnectionResult={dataSourceConnectionResult}
                setupContextResetKey={setupTranscriptResetKey}
                isDisconnectingDataSource={isDisconnectingDataSource}
                isNewUser={isProfileNewUser}
                userDisplayName={userFullName}
                onBookmarksClick={
                  showStandaloneBookmarksEntry
                    ? async () => {
                        const nextOpen = !showBookmarksDropdown;
                        setShowBookmarksDropdown(nextOpen);
                        if (nextOpen) {
                          closeSidebar();
                          // Old standalone favorites dropdown path retained for quick rollback.
                          await loadBookmarks();
                        }
                      }
                    : undefined
                }
                onHistoryClick={() => {
                  setShowBookmarksDropdown(false);
                  openSidebar();
                }}
                onNewChatClick={handleStartNewConversation}
                isHistoryOpen={isSidebarOpen}
                onShowKpiLineage={showKpiLineage}
                onShowChartLineage={showChartLineage}
              />
            </div>
          </>
        </FinanceConversationLayout>
      </main>

      <LineagePanel context={lineageContext} onClose={() => setLineageContext(null)} />

      {/* Export Loading Overlay */}
      {isExporting && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
          <Card className="w-full max-w-md mx-4 shadow-2xl border-2">
            <CardContent className="p-8 flex flex-col items-center justify-center space-y-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg className="h-12 w-12 text-primary animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              </div>
              <div className="text-center space-y-3 w-full">
                <h3 className="text-lg font-semibold text-foreground">
                  Exporting Charts
                </h3>
                {exportProgress && (
                  <>
                    <p className="text-sm font-medium text-foreground">
                      {exportProgress.step}
                    </p>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all duration-300 ease-out rounded-full"
                        style={{ width: `${(exportProgress.current / exportProgress.total) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {exportProgress.current} of {exportProgress.total} steps completed
                    </p>
                  </>
                )}
                {!exportProgress && (
                  <p className="text-sm text-muted-foreground">
                    {exportFormat === 'ppt'
                      ? 'Preparing PowerPoint presentation...'
                      : 'Preparing PDF document...'}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
};

export default FinanceBotV2Dashboard;
